<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEmitter } from 'dashboard/composables/emitter';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MasifiWhatsappQrAPI from 'dashboard/api/masifiWhatsappQr';
import {
  WHATSAPP_QR_EVENT,
  whatsappQrLinkPath,
} from 'dashboard/helper/masifiWhatsappQr';

// E2E fix #5 (Marcos, live test 2026-09-23): FinishSetup.vue (upstream) shows a wa.me test QR for every
// Channel::Whatsapp, which makes no sense for a QR-provider inbox — there is no Business API to test against,
// and the real linking flow (scan the QR, watch the state) already lives one tab away in our own panel.
// FinishSetup.vue hides the upstream QR block for a QR inbox (import + 1 line) and renders this instead.
// UX fix (Marcos 2026-09-23): this block used to say "next step: link your number" even when the number was
// already linked. It now reads the real state (admin API — creating an inbox is admin-only, like the panel) and
// follows it live: linked → a confirmation that explains the native "Take me there"/"More settings" buttons
// below (no extra CTA); not linked → why it matters + one button straight to the Configuration tab (the QR).
const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const getInbox = useMapGetter('inboxes/getInbox');
const inbox = computed(() => getInbox.value(props.inboxId) || {});

// null = not known yet (or unreadable): render nothing rather than guess the wrong next step.
const connection = ref(null);
const linked = computed(() => connection.value === 'open');

const linkedHelp = computed(() =>
  t('MASIFI_WHATSAPP_QR.FINISH_STEP.LINKED_HELP', {
    goTo: t('INBOX_MGMT.FINISH.BUTTON_TEXT'),
    moreSettings: t('INBOX_MGMT.FINISH.MORE_SETTINGS'),
  })
);
const unlinkedHelp = computed(() =>
  t('MASIFI_WHATSAPP_QR.FINISH_STEP.UNLINKED_HELP', {
    button: t('MASIFI_WHATSAPP_QR.FINISH_STEP.BUTTON'),
    path: whatsappQrLinkPath(t, inbox.value.name),
  })
);

// The route param is a string; the ActionCable payload carries a number.
useEmitter(WHATSAPP_QR_EVENT, data => {
  if (String(data?.inbox_id) === String(props.inboxId) && data.connection)
    connection.value = data.connection;
});

onMounted(async () => {
  try {
    const { data } = await MasifiWhatsappQrAPI.get(props.inboxId);
    // A live event that arrived while the GET was in flight is newer: keep it.
    connection.value ??= data?.connection ?? null;
  } catch {
    // Keep null: the native buttons below still work; a wrong "link your number" is what this fix removes.
  }
});
</script>

<template>
  <div
    v-if="linked"
    data-test="wa-qr-finish-linked"
    class="flex flex-col gap-2 items-center mt-8 text-center"
  >
    <p class="text-sm font-medium text-n-teal-11">
      {{
        t('MASIFI_WHATSAPP_QR.FINISH_STEP.LINKED_TITLE', {
          phone: inbox.phone_number,
        })
      }}
    </p>
    <p class="text-sm text-n-slate-11 max-w-lg">{{ linkedHelp }}</p>
  </div>
  <div
    v-else-if="connection"
    data-test="wa-qr-finish-unlinked"
    class="flex flex-col gap-3 items-center mt-8 text-center"
  >
    <p class="text-sm font-medium text-n-amber-11">
      {{ t('MASIFI_WHATSAPP_QR.FINISH_STEP.UNLINKED_TITLE') }}
    </p>
    <p class="text-sm text-n-slate-11 max-w-lg">{{ unlinkedHelp }}</p>
    <router-link
      :to="{
        name: 'settings_inbox_show',
        params: { inboxId, tab: 'configuration' },
      }"
    >
      <NextButton
        data-test="wa-qr-finish-link"
        :label="t('MASIFI_WHATSAPP_QR.FINISH_STEP.BUTTON')"
      />
    </router-link>
  </div>
</template>
